import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import type {DiffEntry, DiffUser} from '@github-ui/diff-lines/types'
import {DiffPlaceholder} from '@github-ui/diffs/DiffParts'
import styles from './PullRequestDiffsList.module.css'
import type {CommentingImplementation, MarkerNavigationImplementation} from '@github-ui/conversations'
import {FilesChangedFilterBlankSlate} from './FilesChangedFilterBlankSlate'
import {useSelectedFullDiffHash} from '@github-ui/diff-lines/use-selected-diff-hash'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import type {Repository} from '@github-ui/current-repository'
import {PullRequestDiff} from './PullRequestDiff'
// eslint-disable-next-line no-restricted-imports
import {getCurrentSize, ScreenSize} from '@github-ui/screen-size'
import {useResizeObserver} from '@primer/react'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {PullRequestDiffEntryErrorFallback} from './PullRequestDiffEntryErrorFallback'

export const SHOW_WHIMSY_THRESHOLD = 15

export interface PullRequestDiffsListProps {
  basePath: string
  commentBoxConfig: CommentingImplementation['commentBoxConfig']
  commentBoxSubject: CommentingImplementation['commentBoxSubject']
  contextLinesURL: string
  currentUser: DiffUser
  diffs: DiffEntry[]
  headBranchName: string
  repository: Pick<Repository, 'id' | 'name' | 'ownerLogin'>
  commentBatchPending: boolean
  endOfDiffsImagePath?: string | undefined
}

const headerHeight = 41
const headerWrapperPadding = 16
const baseDiffHeight = headerHeight + headerWrapperPadding
const stickyHeaderHeight = 60 // height to be used when scrolling, the 4px difference looks nicer
export const PullRequestDiffsList = memo(PullRequestDiffsListUnmemoized)

function PullRequestDiffsListUnmemoized({
  basePath,
  commentBoxConfig,
  commentBoxSubject,
  contextLinesURL,
  currentUser,
  diffs,
  headBranchName,
  repository,
  commentBatchPending,
  endOfDiffsImagePath,
}: PullRequestDiffsListProps) {
  const [diffManuallyExpanded, __] = useState(false)
  const selectedPathDigest = useSelectedFullDiffHash()
  //we only want to run this effect once, so we use a ref to track if we have run it before
  const haveRunEffectOnPageLoad = useRef(false)
  const diffsParentRef = useRef<HTMLDivElement>(null)
  const markerNavigationImplementation: MarkerNavigationImplementation = {
    incrementActiveMarker: () => {},
    decrementActiveMarker: () => {},
    filteredMarkers: [],
    onActivateGlobalMarkerNavigation: () => {},
    activeGlobalMarkerID: undefined,
  }
  // Only works on system settings, not dotcom user settings
  const prefersReducedMotion = usePrefersReducedMotion()
  const showWhimsy = diffs.length > SHOW_WHIMSY_THRESHOLD && endOfDiffsImagePath && !prefersReducedMotion

  useEffect(() => {
    const isHighlightingRightRow = selectedPathDigest.includes('R')
    const isHighlightingLeftRow = selectedPathDigest.includes('L')
    const isHighlightingRow = isHighlightingLeftRow || isHighlightingRightRow
    let pathDigestWithNoLineNumberOrHash = isHighlightingRow
      ? isHighlightingLeftRow
        ? selectedPathDigest.split('L')[0]
        : selectedPathDigest.split('R')[0]
      : selectedPathDigest
    pathDigestWithNoLineNumberOrHash = pathDigestWithNoLineNumberOrHash?.replace('#', '')
    if (haveRunEffectOnPageLoad.current) {
      //we are focused on the same file, no need to do the work again
      return
    }
    haveRunEffectOnPageLoad.current = true
    //remove the leading hash if it exists, also trim the secondary line numbers off so that the first line number can
    // be focused and scrolled to
    const pathDigestWithoutHashOrDash = selectedPathDigest.replace('#', '').split('-')[0]
    const indexOfDiff = getDiffWithPathDigest(pathDigestWithNoLineNumberOrHash ?? selectedPathDigest, diffs)
    if (indexOfDiff === -1) {
      return
    }
    if (isHighlightingRow && focusRowElement(diffsParentRef, pathDigestWithoutHashOrDash ?? '')) {
      //if we are in here, then nothing else to do, as the user was selecting a row already
    } else {
      // otherwise we couldn't find the row to highlight (was a context row)  so default to just highlighting the file itself
      const allDiffLinesElements = diffsParentRef.current?.querySelectorAll(
        `[data-diff-anchor=diff-${pathDigestWithNoLineNumberOrHash}]`,
      )
      if (allDiffLinesElements && allDiffLinesElements.length === 1) {
        const elementToScrollTo = allDiffLinesElements[0] as HTMLElement
        const yOffset =
          elementToScrollTo.getBoundingClientRect().top +
          (ssrSafeWindow?.scrollY ?? 0) -
          baseDiffHeight -
          stickyHeaderHeight
        ssrSafeWindow?.scrollTo({top: yOffset, left: 0})
        const collapseButton = diffsParentRef.current?.querySelectorAll(
          `[aria-label="collapse file: ${diffs[indexOfDiff]?.path}"]`,
        )
        if (collapseButton && collapseButton.length === 1) {
          const collapseButtonToFocus = collapseButton[0] as HTMLElement
          collapseButtonToFocus.focus()
        }
      } else {
        //in case the diff was collapsed, there wouldn't be any diff lines to find, so we
        //instead scroll to the expand button
        const expandButton = diffsParentRef.current?.querySelectorAll(
          `[aria-label="expand file: ${diffs[indexOfDiff]?.path}"]`,
        )
        if (expandButton && expandButton.length === 1) {
          const expandButtonToFocus = expandButton[0] as HTMLElement
          const yOffset =
            expandButtonToFocus.getBoundingClientRect().top +
            (ssrSafeWindow?.scrollY ?? 0) -
            baseDiffHeight -
            stickyHeaderHeight
          ssrSafeWindow?.scrollTo({top: yOffset, left: 0})
          expandButtonToFocus.focus()
        }
      }
    }
  }, [selectedPathDigest, diffs])

  // TODO: implement custom find if necessary, and if not, this can stay undefined
  // but we probably will want custom find for the scenario where a user is viewing
  // a PR that is too big and we need to populate a list of search results from within
  // the PR
  const focusedSearchResult = undefined

  const dynamicSplitPreferenceEnabled = useFeatureFlag('prx_dynamic_split_pref')
  const [diffListWidth, setDiffListWidth] = useState<ScreenSize>(ScreenSize.small)

  // Track diff list width updates
  const updateDiffListWidth = useCallback(() => {
    if (diffsParentRef.current && dynamicSplitPreferenceEnabled) {
      setDiffListWidth(getCurrentSize(diffsParentRef.current.clientWidth))
    }
  }, [dynamicSplitPreferenceEnabled])

  // Use ResizeObserver to track element size changes
  useResizeObserver(updateDiffListWidth, diffsParentRef)

  // Initial width calculation
  useEffect(() => {
    updateDiffListWidth()
  }, [updateDiffListWidth])

  // if the diff list width is less than 768px we auto switch to unified (when FF is enabled)
  const userWithModifiedSplitPreference = useMemo(
    () => ({
      ...currentUser,
      splitPreference:
        dynamicSplitPreferenceEnabled && diffListWidth < ScreenSize.large ? 'unified' : currentUser.splitPreference,
    }),
    [currentUser, dynamicSplitPreferenceEnabled, diffListWidth],
  )

  if (diffs.length === 0) {
    return <FilesChangedFilterBlankSlate />
  }

  return (
    <div ref={diffsParentRef} data-hpc>
      {diffs.map(diffEntry => {
        return (
          <div key={diffEntry.pathDigest} className={styles.entry}>
            <ErrorBoundary fallback={<PullRequestDiffEntryErrorFallback {...diffEntry} />}>
              <PullRequestDiff
                commentBoxConfig={commentBoxConfig}
                commentBoxSubject={commentBoxSubject}
                commentBatchPending={commentBatchPending}
                headBranchName={headBranchName}
                basePath={basePath}
                changeType={diffEntry.status}
                collapsed={diffEntry.collapsed ?? false}
                commentingEnabled
                contextLinesURL={contextLinesURL}
                currentUser={userWithModifiedSplitPreference}
                diffContext="pr"
                diffLines={diffEntry.diffLines}
                diffManuallyExpanded={diffManuallyExpanded}
                diffMatches={diffEntry.diffMatches}
                diffSize={diffEntry.diffSize}
                focusedSearchResult={focusedSearchResult}
                helpUrl={diffEntry.helpUrl}
                isBinary={diffEntry.isBinary}
                isSubmodule={diffEntry.isSubmodule}
                isTooBig={diffEntry.isTooBig}
                linesAdded={diffEntry.linesAdded}
                linesChanged={diffEntry.linesChanged}
                linesDeleted={diffEntry.linesDeleted}
                markerNavigationImplementation={markerNavigationImplementation}
                newCommitOid={diffEntry.newCommitOid}
                newTreeEntry={diffEntry.newTreeEntry}
                objectId={diffEntry.objectId}
                oldCommitOid={diffEntry.oldCommitOid}
                oldTreeEntry={diffEntry.oldTreeEntry}
                path={diffEntry.path}
                pathDigest={diffEntry.pathDigest}
                repository={repository}
                richDiff={diffEntry.richDiff}
                status={diffEntry.status}
                submodule={diffEntry.submodule}
                truncatedReason={diffEntry.truncatedReason}
              />
            </ErrorBoundary>
          </div>
        )
      })}
      {/* TODO: Create a static image to render in place of the GIF if user prefers reduced motion. */}
      {showWhimsy && <EndOfDiffsMessage imagePath={endOfDiffsImagePath} />}

      {/* DiffPlaceholder is used for the skeleton placeholder when a diff isn't loaded.
      It needs to be somewhere on the page so that it can be drawn from within the diff lines component. */}
      <DiffPlaceholder />
    </div>
  )
}

function getDiffWithPathDigest(pathDigest: string, diffEntries: DiffEntry[]) {
  for (let i = 0; i < diffEntries.length; i++) {
    if (pathDigest === diffEntries[i]?.pathDigest) return i
  }
  return -1
}

function focusRowElement(diffsParentRef: React.RefObject<HTMLDivElement>, selectedPathDigest: string) {
  const lineWithinDiffToHighlight = diffsParentRef.current?.querySelectorAll(
    `[data-line-anchor=diff-${selectedPathDigest}]`,
  )
  if (lineWithinDiffToHighlight && lineWithinDiffToHighlight.length === 1) {
    const rowToFocus = lineWithinDiffToHighlight[0] as HTMLElement
    rowToFocus.focus()
    return true
  }
  return false
}

function EndOfDiffsMessage({imagePath, imageAlt}: {imagePath: string; imageAlt?: string | undefined}): JSX.Element {
  return (
    <div className="d-flex flex-justify-center flex-column text-center flex-items-center py-8 px-3">
      <img src={imagePath} alt={imageAlt || 'GIF of an octocat high fiving another octocat'} style={{height: '56px'}} />
      <span className="fgColor-muted mt-2">You made it to the end!</span>
    </div>
  )
}

try{ PullRequestDiffsList.displayName ||= 'PullRequestDiffsList' } catch {}
try{ PullRequestDiffsListUnmemoized.displayName ||= 'PullRequestDiffsListUnmemoized' } catch {}
try{ EndOfDiffsMessage.displayName ||= 'EndOfDiffsMessage' } catch {}